import { FileText, Sparkles, Bug, Wrench, Star } from "lucide-react";

import "../styles/marketing.css";
import Seo, { breadcrumbJsonLd } from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";

const releases = [
  {
    version: "2.8.0",
    date: "April 2026",
    type: "feature",
    title: "Call Masking Inbound Routing",
    items: [
      "Customers can now call back the masked number and be automatically routed to their assigned technician",
      "Added fallback number configuration — calls route to your office when technicians are unavailable",
      "Inbound calls are logged in job history with recording support",
      "Edge case handling: completed jobs, unknown callers, multiple active jobs",
    ],
  },
  {
    version: "2.7.0",
    date: "April 2026",
    type: "feature",
    title: "Push Notifications Overhaul",
    items: [
      "Fixed push notifications for all user types — owners, admins, and technicians",
      "Web push (OneSignal) now always attempts delivery regardless of DB subscription state",
      "Added native app Expo token bridge for mobile push registration",
      "Technician portal now supports push notifications on both web and mobile",
      "Improved test notification feedback with delivery status details",
    ],
  },
  {
    version: "2.6.0",
    date: "April 2026",
    type: "feature",
    title: "Technician Financials Fix & Job Filters",
    items: [
      "Technician financial page now matches admin report calculations exactly",
      "Added processing fees, settlements, adjustments, and balance tracking for technicians",
      "Added technician filter dropdown on Jobs page (Calendar & List views)",
      "Jobs can now be filtered by assigned technician or 'Unassigned'",
    ],
  },
  {
    version: "2.5.0",
    date: "March 2026",
    type: "feature",
    title: "Jobs Page UI/UX Overhaul",
    items: [
      "Redesigned job cards with modern glassmorphic card layout and animations",
      "Added date navigator with prev/today/next/all navigation for list view",
      "Calendar view updated with new card design and job details display",
      "Improved mobile responsiveness across all job views",
    ],
  },
  {
    version: "2.4.0",
    date: "March 2026",
    type: "feature",
    title: "Demo Booking & SMS Templates",
    items: [
      "Added 'Book a Demo' modal on landing page with available time slots",
      "Super Admin demo management panel with status tracking",
      "Centralized all SMS templates in Settings > Templates tab",
      "Fixed SMS placeholders (job_city, job_zip) to read from DB fields",
      "Added Review Request SMS button to job details",
    ],
  },
  {
    version: "2.3.0",
    date: "February 2026",
    type: "feature",
    title: "Customer Portal & Invoicing",
    items: [
      "Launched customer portal for job tracking and invoice viewing",
      "PDF invoice generation with company branding",
      "Estimate to invoice conversion workflow",
      "Customer portal authentication with secure token-based access",
    ],
  },
  {
    version: "2.2.0",
    date: "January 2026",
    type: "feature",
    title: "Call Masking & PIN System",
    items: [
      "Introduced PIN-based call masking for outbound technician calls",
      "Automatic PIN generation on job assignment, deactivation on completion",
      "Call recording with whisper message support",
      "Masked call logging in job history for complete audit trail",
    ],
  },
  {
    version: "2.1.0",
    date: "January 2026",
    type: "improvement",
    title: "Automation Engine",
    items: [
      "Built automation system for job lifecycle notifications",
      "Appointment reminders with configurable timing",
      "Automatic technician assignment notifications via SMS",
      "Job status change email notifications to customers",
    ],
  },
  {
    version: "2.0.0",
    date: "December 2025",
    type: "feature",
    title: "Platform Launch",
    items: [
      "Core CRM with job management, scheduling, and dispatching",
      "Twilio-powered phone system with SMS and voice",
      "Stripe billing integration with subscription management",
      "Technician portal with earnings tracking",
      "Financial reporting dashboard with technician breakdowns",
    ],
  },
];

const typeConfig = {
  feature: { icon: Sparkles, label: "New Feature", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  improvement: { icon: Star, label: "Improvement", color: "bg-blue-50 text-blue-700 border-blue-200" },
  bugfix: { icon: Bug, label: "Bug Fix", color: "bg-amber-50 text-amber-700 border-amber-200" },
  maintenance: { icon: Wrench, label: "Maintenance", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const ReleaseNotesPage = () => {
  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="release-notes-page-root">
      <Seo
        title="Release Notes — What's New in Fieldz AI"
        description="The Fieldz AI changelog: new features, improvements, and fixes across scheduling, dispatch, phone system, invoicing, and the technician app."
        path="/release-notes"
        jsonLd={breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Release Notes", path: "/release-notes" }])}
      />
      <MarketingNav />

      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white text-center px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5 flex items-center justify-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> Changelog
        </p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-4">Release Notes</h1>
        <p className="text-lg text-slate-600 max-w-xl mx-auto">Stay up to date with the latest features, improvements, and fixes</p>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <div className="absolute left-[19px] top-0 bottom-0 w-px bg-slate-200" />
            <div className="space-y-6">
              {releases.map((r, i) => {
                const cfg = typeConfig[r.type] || typeConfig.feature;
                return (
                  <div key={i} className="relative pl-12" data-testid={`release-${r.version}`}>
                    <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center z-10">
                      <cfg.icon className="w-4 h-4 text-[var(--mk-blue)]" />
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_6px_20px_-8px_rgba(15,23,42,0.08)]">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${cfg.color}`}>{cfg.label}</span>
                        <span className="text-xs font-mono text-slate-400">v{r.version}</span>
                        <span className="text-xs text-slate-400">— {r.date}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{r.title}</h3>
                      <ul className="space-y-1.5">
                        {r.items.map((item, j) => (
                          <li key={j} className="text-slate-600 text-sm flex gap-2">
                            <span className="text-[var(--mk-blue)] mt-1.5 shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ReleaseNotesPage;
